import type { Competitor } from '../types';

// Researched 2026-11-03. Every claim links to the page it came from.
// To correct something: change it here, update sourceUrl and lastReviewed.

export const hex: Competitor = {
  "id": "hex",
  "name": "Hex",
  "group": "bi",
  "website": "https://hex.tech",
  "owner": "Hex Technologies, Inc. (independent, venture-backed; $100M Series D, October 2026)",
  "summary": "Browser-based, collaborative SQL and Python notebook that turns analysis into published data apps. It also offers conversational AI (Threads) and semantic models. Built mainly for data analysts and data scientists.",
  "bestFor": [
    "Data teams whose analysis mixes SQL and Python, such as forecasting, statistics or ML, in one place",
    "Analysts who build interactive data apps or one-off investigations for stakeholders",
    "Small teams that want a free tier or published self-serve pricing ($36-$75 per editor per month)"
  ],
  "notFor": [
    "Finance or ops users who want an Excel-like grid with cell-level formulas",
    "Business users who need to enter or edit data directly in a governed table",
    "Organizations that require self-hosting"
  ],
  "strengths": [
    "Python is native: notebooks combine SQL and Python cells, and the AI agent can write and run Python in Threads. Sigma has no comparable notebook.",
    "Public, self-serve pricing with a free Community tier, so a team can start without a sales cycle.",
    "Project-level git sync to GitHub or GitLab (projects stored as YAML), with optional required pull requests before publishing.",
    "AI agents for notebooks, semantic modeling and Slack and MCP access, with Context Studio for managing the context the agent uses.",
    "Better suited to exploratory, code-heavy analysis and custom data apps than dashboard-style BI."
  ],
  "limitations": [
    "Calculations are column-level formulas with no single-cell or range references and no window functions. It is not a true spreadsheet.",
    "Writeback cells write dataframes back to the database. They are configured by editors, not used by business users to enter data, and some warehouses (e.g. Athena, ClickHouse, Trino) are not supported.",
    "Scheduled runs and Slack notifications need the Team or Enterprise plan; Professional allows one scheduled run per project.",
    "Per-user warehouse permissions through Snowflake OAuth need the Enterprise plan, and credential sharing must be off.",
    "Signed embedding for external users is an Enterprise add-on.",
    "Hex 4.0, a ground-up rebuild announced Oct 2026, is in private beta, so the product may change substantially."
  ],
  "pricing": {
    "model": "per editor seat, plus usage-based compute and AI credits",
    "publicStartingPrice": "$36/editor/month (Professional); Team $75/editor/month",
    "freeTier": "yes: Community plan, free forever, up to 5 published apps, limited AI credits; 14-day Team trial",
    "notes": "Tiers are Community, Professional, Team and Enterprise (custom). Cost is driven by editor seats, extra compute and AI credits. Enterprise adds SSO, Explorer seats as an add-on, and the embedded analytics add-on. Plan pricing is listed per editor; a separate viewer price was not listed.",
    "sourceUrl": "https://hex.tech/pricing",
    "checkedOn": "2026-11-03"
  },
  "features": {
    "live_warehouse_query": {
      "value": "yes",
      "note": "SQL cells query the warehouse. Query mode pushes chart and pivot work down to it; query results can be cached.",
      "sourceUrl": "https://learn.hex.tech/docs/cell-types/sql-cells/overview"
    },
    "spreadsheet_interface": {
      "value": "partial",
      "note": "Spreadsheet-style column formulas only: no cell or range references and no window functions.",
      "sourceUrl": "https://learn.hex.tech/docs/explore-data/cells/calculations",
      "badge": "Column formulas only"
    },
    "sql_editor": {
      "value": "yes",
      "note": "Warehouse SQL cells, plus DuckDB SQL over dataframes.",
      "sourceUrl": "https://learn.hex.tech/docs/cell-types/sql-cells/overview"
    },
    "python_support": {
      "value": "yes",
      "note": "Python cells in notebooks. The agent can also run Python in Threads (Mar 2026).",
      "sourceUrl": "https://learn.hex.tech/changelog/2026-03-24"
    },
    "writeback": {
      "value": "partial",
      "note": "Editors can write dataframes to the warehouse with writeback cells. No form-style data entry for business users was found.",
      "sourceUrl": "https://learn.hex.tech/docs/explore-data/cells/data-cells/writeback-cells",
      "badge": "Editors only"
    },
    "semantic_layer": {
      "value": "yes",
      "note": "Semantic models with a Modeling Agent.",
      "sourceUrl": "https://hex.tech/blog/fall-2025-launch/"
    },
    "embedded_analytics": {
      "value": "yes",
      "note": "Signed embedding with row-level security for external users. The pricing page lists it as an Enterprise add-on.",
      "sourceUrl": "https://learn.hex.tech/docs/share-insights/embedding"
    },
    "ai_assistant": {
      "value": "yes",
      "note": "Notebook Agent, Threads, Modeling Agent and Chat with App; Generative Apps are in beta. Usage draws on monthly credits.",
      "sourceUrl": "https://learn.hex.tech/docs/getting-started/ai-overview"
    },
    "version_control_git": {
      "value": "yes",
      "note": "Git sync with GitHub or GitLab. Plan tier not stated in the docs.",
      "sourceUrl": "https://learn.hex.tech/docs/versioning/git-sync"
    },
    "open_source_or_self_host": {
      "value": "no",
      "note": "Hosted by Hex. A single-tenant option on AWS is available; no self-host or open-source edition was found.",
      "sourceUrl": "https://hex.tech/security/"
    },
    "desktop_authoring_required": {
      "value": "no",
      "note": "Web app (app.hex.tech).",
      "sourceUrl": "https://learn.hex.tech/docs/getting-started"
    },
    "scheduled_delivery": {
      "value": "partial",
      "note": "Scheduled runs with app subscriptions and conditional notifications need Team or Enterprise. File attachments are not documented.",
      "sourceUrl": "https://learn.hex.tech/docs/share-insights/scheduled-runs-and-notifications",
      "badge": "Team plan and up"
    },
    "row_level_security": {
      "value": "partial",
      "note": "RLS in signed embeds. Internally, per-user warehouse permissions need Snowflake OAuth (Enterprise) with credential sharing off.",
      "sourceUrl": "https://learn.hex.tech/docs/connect-to-data/data-connections/snowflake-oauth"
    },
    "excel_export": {
      "value": "unknown",
      "note": "No Hex documentation found on CSV or Excel download.",
      "sourceUrl": "https://learn.hex.tech/docs/getting-started"
    }
  },
  "recentChanges": [
    {
      "date": "2025-10",
      "change": "Fall launch: Threads conversational analytics in public beta, a Modeling Agent for semantic models, and Notebook Agent upgrades.",
      "sourceUrl": "https://hex.tech/blog/fall-2025-launch/"
    },
    {
      "date": "2026-03",
      "change": "Threads can now run Python; added subagents and an open-beta Hex CLI.",
      "sourceUrl": "https://learn.hex.tech/changelog/2026-03-24"
    },
    {
      "date": "2026-04",
      "change": "Agent memory, starting threads from data apps, a GitHub action to sync guides, and semantic model usage references.",
      "sourceUrl": "https://learn.hex.tech/changelog/2026-04-14"
    },
    {
      "date": "2026-05",
      "change": "Signed embedding with row-level security for Generative apps.",
      "sourceUrl": "https://learn.hex.tech/changelog/2026-05-21"
    },
    {
      "date": "2026-10",
      "change": "$100M Series D at a $1.2B valuation (Peak XV, 137 Ventures) and Hex 4.0, an agent-focused rebuild in private beta.",
      "sourceUrl": "https://www.cooley.com/news/coverage/2026/2026-10-30-hex-raises-100-million-series-d-at-1-2b-valuation"
    }
  ],
  "sources": [
    {
      "title": "Hex pricing",
      "url": "https://hex.tech/pricing"
    },
    {
      "title": "Hex fall 2025 launch",
      "url": "https://hex.tech/blog/fall-2025-launch/"
    },
    {
      "title": "Hex changelog 2026-03-24",
      "url": "https://learn.hex.tech/changelog/2026-03-24"
    },
    {
      "title": "Hex changelog 2026-04-14",
      "url": "https://learn.hex.tech/changelog/2026-04-14"
    },
    {
      "title": "Hex changelog 2026-05-21",
      "url": "https://learn.hex.tech/changelog/2026-05-21"
    },
    {
      "title": "Hex docs: AI overview",
      "url": "https://learn.hex.tech/docs/getting-started/ai-overview"
    },
    {
      "title": "Hex docs: SQL cells",
      "url": "https://learn.hex.tech/docs/cell-types/sql-cells/overview"
    },
    {
      "title": "Hex docs: Calculations",
      "url": "https://learn.hex.tech/docs/explore-data/cells/calculations"
    },
    {
      "title": "Hex docs: Writeback cells",
      "url": "https://learn.hex.tech/docs/explore-data/cells/data-cells/writeback-cells"
    },
    {
      "title": "Hex docs: Git sync",
      "url": "https://learn.hex.tech/docs/versioning/git-sync"
    },
    {
      "title": "Hex docs: Embedding",
      "url": "https://learn.hex.tech/docs/share-insights/embedding"
    },
    {
      "title": "Hex docs: Scheduled runs",
      "url": "https://learn.hex.tech/docs/share-insights/scheduled-runs-and-notifications"
    },
    {
      "title": "Hex docs: Snowflake OAuth",
      "url": "https://learn.hex.tech/docs/connect-to-data/data-connections/snowflake-oauth"
    },
    {
      "title": "Hex security",
      "url": "https://hex.tech/security/"
    },
    {
      "title": "Cooley: Hex Series D (third party)",
      "url": "https://www.cooley.com/news/coverage/2026/2026-10-30-hex-raises-100-million-series-d-at-1-2b-valuation"
    },
    {
      "title": "RuntimeWire: Hex 4.0 (third party)",
      "url": "https://runtimewire.com/article/hex-100m-agent-analytics-rebuild"
    }
  ],
  "lastReviewed": "2026-11-03"
};
